import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { DNABox, DNAButton, DNACard, DNAText, Iffy, luxColors } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { CommonIcon, TitleBar } from 'src/components/DNA';
import { TenantORM, useTenantList } from 'src/state/redux/selector/tenant';
import DNARouteTabs from 'src/components/DNA/Navigation/DNARouteTabs/DNARouteTabs'
import { Switch, Route, useLocation } from 'src/router';
import { useDispatch } from 'src/state/redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import NewTenantModal from './NewTenantModal';
// Tabs
import Users, { UserActions } from './Tabs/Users';
import Integrations from './Tabs/Integrations';
import Configuration from './Tabs/Configuration';
import Reports from './Tabs/Reports'
import Events from './Tabs/Events'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

const styles = StyleSheet.create({
  crumbsColor: {
    color: luxColors.flat.primary,
  },
  header: {
    marginBottom: 16,
  },
  itemTextColor: {
    color: luxColors.contentText.tertiary,
  },
  mainWrapper: {
    paddingHorizontal: 33,
    paddingVertical: 24,
    backgroundColor: luxColors.backgroundColor.secondary,
    flex: 1,
    flexDirection: 'column',
    minWidth: 'calc(100vw - 10px)',
    overflowX: 'auto',
  },
  tabContainer: {
    backgroundColor: luxColors.emptyStateButtonText.primary,
  },
  tab: {
    paddingHorizontal: 12,
    paddingVertical: 20,
    backgroundColor: luxColors.emptyStateButtonText.primary,
  },
  tenantRowWrapper: {
    borderBottomColor: luxColors.divider.primary,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  tenantsWrapper: {
    borderRadius: 4,
    overflow: 'hidden',
  },
});

interface TenantRowProps {
  tenant: TenantORM;
  setSelectedTenant: (tenant: TenantORM) => void;
}

const TenantRow = (props: TenantRowProps) => {
  const { tenant, setSelectedTenant } = props;

  function handleSelectedTenant(): void {
    setSelectedTenant(tenant);
  }

  return (
    <DNACard
      appearance="flat"
      style={styles.tenantRowWrapper}
      interactive="pressableAlt"
      onPress={handleSelectedTenant}
    >
      <DNAText style={styles.itemTextColor}>{tenant.tenant.name}</DNAText>
      {/* [TODO]: TO BE APPLIED IN FOLLOWING TASKS */}
      {/* <DNAButton */}
      {/*  status="tertiary" */}
      {/*  iconLeft="dots-three-vertical-bold" */}
      {/* /> */}
    </DNACard>
  );
};

const Tenants = () => {
  const tenants = useTenantList();
  const [selectedTenant, setSelectedTenant] = useState<TenantORM | undefined>();
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedTenant) {
      const tenant = tenants.find((tenant) => tenant.tenant.id === selectedTenant.tenant.id);
      setSelectedTenant(tenant);
    }
  }, [tenants]);

  function unSelectTenant(): void {
    setSelectedTenant(undefined);
  }

  function addOrganizationHandler(): void {
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: true,
      component: (props) => (
        <NewTenantModal
          {...props}
        />
      ),
    }),
    )
  }

  return (
    <View style={styles.mainWrapper}>
      { /* HEADER */}
      <DNABox spacing="between" alignY="center" style={styles.header}>
        <DNABox alignY="center">
          <Pressable onPress={unSelectTenant}>
            <DNAText style={styles.crumbsColor}>
              Organizations
            </DNAText>
          </Pressable>
          <Iffy is={selectedTenant}>
            <CommonIcon variant="caret-right" status="tertiary" />
            <DNAText style={styles.crumbsColor}>
              {selectedTenant?.tenant.name}
            </DNAText>
          </Iffy>
        </DNABox>
      </DNABox>
      { /* TENANTS LIST */}
      <Iffy is={!selectedTenant}>
        <DNABox appearance="col">
          <DNABox
            appearance="row"
            fill
            spacing="between"
            alignY="center"
            style={{ paddingVertical: 14, paddingHorizontal: 16, backgroundColor: colors['color-text-white'] }}
          >
            <DNAText bold style={styles.itemTextColor}>Name</DNAText>
            <DNAButton
              appearance="outline"
              status="tertiary"
              onPress={addOrganizationHandler}
            >
              Create Organization
            </DNAButton>
          </DNABox>
          <DNABox>
            <DNACard style={styles.tenantsWrapper}>
              { tenants
                .sort((a, b) => a.tenant.name.localeCompare(b.tenant.name))
                .map((tenant) =>
                  <TenantRow setSelectedTenant={setSelectedTenant} key={tenant.tenant.id} tenant={tenant}/>)
            }
            </DNACard>
          </DNABox>
        </DNABox>
      </Iffy>
      { /* USERS LIST */}
      <Iffy is={selectedTenant}>
        <NestedRoutes selectedTenant={selectedTenant} />
      </Iffy>
    </View>
  );
};

const DNATenantsSwitcher: React.FC<{
  selectedTenant: TenantORM | undefined
}> = (props) => {
  const { selectedTenant } = props;
  const featureFlags = useFeatureFlags()
  const location = useLocation()

  if (!selectedTenant) return null;

  return (
    <DNABox appearance="col">
      <Iffy is={selectedTenant && location.pathname === '/tenants'}>
        <DNABox style={{ paddingHorizontal: 24, backgroundColor: colors['color-text-basic'] }}>
          <TitleBar
            title="Admin"
            description="Manage your users and integrations"
          >
            <TitleBar.Button>
              <UserActions tenantId={selectedTenant.tenant.id} />
            </TitleBar.Button>
          </TitleBar>
        </DNABox>
      </Iffy>
      <DNABox alignX="center" fill>
        <DNABox fill>
          <DNACard style={styles.tabContainer} appearance="outline">
            <DNARouteTabs.Router>
              <DNARouteTabs.Tabs withDivider>
                <DNARouteTabs.Tab title="Users" path="/tenants">
                  <Users tenantId={selectedTenant.tenant.id} />
                </DNARouteTabs.Tab>
                <DNARouteTabs.Tab title="Integrations" path="/tenants/integrations">
                  <Integrations selectedTenant={selectedTenant} variant="admin" />
                </DNARouteTabs.Tab>
                <DNARouteTabs.Tab title="Settings" path="/tenants/configuration">
                  <Configuration tenantId={selectedTenant.tenant.id} />
                </DNARouteTabs.Tab>
                <DNARouteTabs.Tab title="Reports" path="/tenants/reports">
                  <Reports tenantId={selectedTenant.tenant.id} />
                </DNARouteTabs.Tab>
                {featureFlags.BEAC_6085_Training_Event
                  ? <DNARouteTabs.Tab title="Events" path="/tenants/events">
                    <Events tenantORM={selectedTenant} />
                  </DNARouteTabs.Tab>
                  : <></>
              }
                <DNARouteTabs.Unmatched rewrite="/tenants" />
              </DNARouteTabs.Tabs>
            </DNARouteTabs.Router>
          </DNACard>
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

const NestedRoutes : React.FC<{
  selectedTenant: TenantORM | undefined
}> = (props) => {
  return (
    <Switch>
      <Route exact path="/tenants" component={() => <DNATenantsSwitcher {...props} /> } />
      <Route exact path="/tenants/integrations" component={() => <DNATenantsSwitcher {...props} /> } />
      <Route exact path="/tenants/configuration" component={() => <DNATenantsSwitcher {...props} /> } />
      <Route exact path="/tenants/reports" component={() => <DNATenantsSwitcher {...props} /> } />
      <Route exact path="/tenants/events" component={() => <DNATenantsSwitcher {...props} /> } />
    </Switch>
  )
}

Tenants.displayName = 'Tenants';

export default Tenants;
